import { createFileRoute } from '@tanstack/react-router';
import { useState, type ReactElement } from 'react';

import { profileName } from '@/components/Avatar';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton } from '@/components/Skeleton';
import { ApiTokensAdminSection } from '@/components/admin/ApiTokensAdminSection';
import {
  AdminUserDetail,
  type AdminUserAction,
} from '@/components/admin/AdminUserDetail';
import { AdminUserInitials } from '@/components/admin/AdminUserInitials';
import { announce } from '@/lib/announce';
import {
  useAdminUser,
  useDeactivateAdminUser,
  useReactivateAdminUser,
  useRevokeAdminUserSessions,
  useSetAdminUserInitials,
} from '@/lib/admin';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useSession } from '@/lib/session';

/**
 * `/admin/users/$userId` — SA-9's account page, rendered through `/admin`'s
 * `<Outlet/>`. The parent has already refused every visitor but a superadmin
 * (`routes/admin.tsx`), so this component never renders for anyone else; the
 * read is still gated inside `useAdminUser` on the same predicate.
 *
 * Each act announces its outcome through the shell's one live region
 * (`announce`, `LiveRegion`), never a region of its own. A refusal — a 409 for
 * D11's last-admin rule, or a flag granted since the page loaded — is kept in
 * state and shown as a `Notice` above the actions with the server's own
 * sentence; any other failure is shown the same way, because the server's
 * message is the only accurate account of what happened.
 *
 * B85, AT-12 — the account's API tokens (`ApiTokensAdminSection`) render after
 * Memberships, through `AdminUserDetail`'s slot. The section reads and acts on
 * its own: its list is gated inside `useAdminApiTokens` like the detail read.
 *
 * Member initials (`docs/notes/member-initials.md`) — the "Avatar initials"
 * control (`AdminUserInitials`) renders through `AdminUserDetail`'s `initials`
 * slot. Its save is `useSetAdminUserInitials`; its outcome is announced through
 * the same live region, and its refusal (a 422 the client check missed, or the
 * 404 of an account deleted since the page loaded) is kept in its own state, so
 * it shows beside the field and not above the lifecycle actions.
 */

export const Route = createFileRoute('/admin/users/$userId')({
  component: AdminUserRoute,
});

function AdminUserRoute(): ReactElement {
  const { userId } = Route.useParams();
  const { t } = useInterfaceLanguage();
  const { profile } = useSession();
  const detail = useAdminUser(userId);
  const deactivate = useDeactivateAdminUser(userId);
  const reactivate = useReactivateAdminUser(userId);
  const revoke = useRevokeAdminUserSessions(userId);
  const setInitials = useSetAdminUserInitials(userId);
  const [pendingAction, setPendingAction] = useState<AdminUserAction | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  // Tagged with the account it belongs to: this component is NOT remounted
  // when `$userId` changes (TanStack Router keys a match by route id), so a
  // bare string would carry one account's refusal onto the next.
  const [initialsRefusal, setInitialsRefusal] = useState<{
    readonly userId: string;
    readonly message: string;
  } | null>(null);

  if (detail.error !== null) {
    return (
      <EmptyState
        tone="error"
        size="panel"
        title={t('admin.user.loadFailed')}
        body={detail.error.message}
        action={
          <Button type="button" onClick={() => void detail.refetch()}>
            {t('admin.error.retry')}
          </Button>
        }
      />
    );
  }

  if (detail.data === undefined || profile === null) {
    return (
      <div aria-busy="true" className="mx-auto flex w-full max-w-4xl flex-col gap-4 py-2">
        <span className="sr-only">{t('admin.user.loading')}</span>
        <Skeleton shape="avatar" />
        <Skeleton shape="block" count={2} />
      </div>
    );
  }

  const user = detail.data;
  const name = profileName(user);

  async function perform(action: AdminUserAction, reason: string): Promise<void> {
    const body = { confirm: true as const, reason };
    setPendingAction(action);
    setRefusal(null);
    try {
      if (action === 'deactivate') {
        await deactivate.mutateAsync(body);
        announce(t('admin.user.announce.deactivated', { name }));
      } else if (action === 'reactivate') {
        await reactivate.mutateAsync(body);
        announce(t('admin.user.announce.reactivated', { name }));
      } else {
        const { sessionsRevoked } = await revoke.mutateAsync(body);
        announce(
          t(
            'admin.user.announce.sessionsRevoked',
            { count: sessionsRevoked, name },
            sessionsRevoked,
          ),
        );
      }
    } catch (error) {
      const message = (error as { readonly message?: unknown }).message;
      setRefusal(typeof message === 'string' ? message : String(error));
    } finally {
      setPendingAction(null);
    }
  }

  async function saveInitials(initials: string | null): Promise<boolean> {
    setInitialsRefusal(null);
    try {
      await setInitials.mutateAsync({ initials });
      announce(
        initials === null
          ? t('admin.initials.announce.cleared', { name })
          : t('admin.initials.announce.set', { name, initials }),
      );
      return true;
    } catch (error) {
      const message = (error as { readonly message?: unknown }).message;
      setInitialsRefusal({
        userId,
        message: typeof message === 'string' ? message : String(error),
      });
      return false;
    }
  }

  return (
    <AdminUserDetail
      user={user}
      viewerUserId={profile.id}
      pendingAction={pendingAction}
      refusal={refusal}
      onConfirm={perform}
      initials={
        <AdminUserInitials
          // A fresh draft per account: see `initialsRefusal` above.
          key={user.id}
          user={user}
          pending={setInitials.isPending}
          refusal={initialsRefusal?.userId === user.id ? initialsRefusal.message : null}
          onSave={saveInitials}
        />
      }
    >
      <ApiTokensAdminSection user={user} />
    </AdminUserDetail>
  );
}
